'use client'

import { Skeleton } from '@lastbrand/web-ui/skeleton'
import { css } from '@lastbrand/web-ui-styled-system/css'
import classnames from 'classnames'
import { forwardRef, memo, useMemo } from 'react'

import { getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { SvdProductCard } from '@src/components/svd-product-card'
import { isPlSvdCard, mapPlSvdCard, type PlSvdCard } from '@src/components/svd-product-card/map-pl-svd-card'
import { getSvdCardListKey } from '@src/components/svd-product-card/svd-product-card.helpers'
import { ProductCard } from '@src/ds/blocks/product-card'
import { trackingMetaData } from '@src/pages/[...slug]/components/collection/collection.constants'
import { QUINCE_BOT_LOCATORS } from '@src/quince-site-common/src/constants/quinceBot'
import { useIsSvdEnabled } from '@src/quince-site-common/src/hooks/use-is-svd-enabled'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import type { SubCollectionSectionProps } from './sub-collection-section-ds.types'

const TRACKING_CATEGORY_NAVIGATION = 'navigation'
// Drive max widths from DS spacing token vars.
const PRODUCT_CARD_MAX_WIDTH_DESKTOP = 'calc(var(--web-ui-spacing-space-56) * 10)'
const CONTENT_MAX_WIDTH = 'calc(var(--web-ui-spacing-space-56) * 43)'
// const SUBTITLE_MAX_WIDTH_DESKTOP = 'calc(var(--web-ui-spacing-space-56) * 23)'
// const SUBTITLE_MAX_WIDTH_MOBILE = 'calc(var(--web-ui-spacing-space-56) * 9)'

const skeletonItemCss = {
  // eslint-disable-next-line i18next/no-literal-string -- Panda width token (not user-facing copy)
  w: 'full',
  maxW: { lg: PRODUCT_CARD_MAX_WIDTH_DESKTOP },
  h: { base: '29.3rem', lg: '50rem' },
}

// eslint-disable-next-line i18next/no-literal-string -- Panda layout tokens (not user-facing copy)
const innerClass = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space-48',
  w: 'full',
  maxW: CONTENT_MAX_WIDTH,
})

// // eslint-disable-next-line i18next/no-literal-string -- Panda layout tokens (not user-facing copy)
// const titleBlockClass = css({
//   display: 'flex',
//   flexDirection: 'column',
//   alignItems: 'center',
//   mb: { base: 'space-32', lg: 'space-56' },
//   w: 'full',
// })

// // eslint-disable-next-line i18next/no-literal-string -- Panda layout tokens (not user-facing copy)
// const titleBlockExperimentClass = css({
//   alignItems: 'stretch',
//   py: { base: 'space-24', lg: 'space-32' },
//   mb: '0',
//   borderTopWidth: { base: '0', lg: '1px' },
//   borderTopStyle: 'solid',
//   borderTopColor: 'lightgray',
// })

// // eslint-disable-next-line i18next/no-literal-string -- Panda text style tokens (not user-facing copy)
// const titleTextClass = css({
//   textAlign: 'center',
//   textTransform: 'uppercase',
//   textStyle: { base: 'title-12', lg: 'title-14' },
//   mb: { base: 'space-12', lg: 'space-4' },
// })

// // eslint-disable-next-line i18next/no-literal-string -- Panda text style tokens (not user-facing copy)
// const titleTextExperimentClass = css({
//   textAlign: { base: 'center', lg: 'left' },
//   textStyle: { base: 'title-12', lg: 'title-14' },
//   color: 'darkgray',
// })

// // eslint-disable-next-line i18next/no-literal-string -- Panda layout tokens (not user-facing copy)
// const subTitleClass = css({
//   display: 'flex',
//   justifyContent: 'center',
//   alignItems: 'flex-end',
//   textAlign: 'center',
//   maxW: { base: SUBTITLE_MAX_WIDTH_MOBILE, lg: SUBTITLE_MAX_WIDTH_DESKTOP },
//   textStyle: 'body-12',
// })

// // eslint-disable-next-line i18next/no-literal-string -- Panda layout tokens (not user-facing copy)
// const subTitleExperimentClass = css({
//   justifyContent: { base: 'center', lg: 'flex-start' },
//   textAlign: { base: 'center', lg: 'left' },
//   maxW: 'none',
//   textStyle: 'body-12',
//   color: 'darkgray',
// })

// // eslint-disable-next-line i18next/no-literal-string -- Panda spacing tokens (not user-facing copy)
// const pseudoTitleSpacingClass = css({
//   mt: {
//     base: 'space-10',
//     lg: 'space-32',
//   },
// })

// eslint-disable-next-line i18next/no-literal-string -- Panda layout tokens (not user-facing copy)
const gridClass = css({
  display: 'grid',
  w: 'full',
  gridTemplateColumns: {
    base: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(4, minmax(0, 1fr))',
  },
  justifyItems: 'center',
  // Figma Widget-ProductGrid (3764:14199 desktop / 3764:16636 mobile): 16/32 mobile, 24/48 desktop.
  columnGap: { base: 'space-16', lg: 'space-24' },
  rowGap: { base: 'space-32', lg: 'space-48' },
  '& > *': {
    // content-visibility forces paint containment, which clips focus rings flush at the item's
    // padding box (the card's own clip-path allowance can't help there) — the padding/negative-
    // margin pair moves that clip edge 8px out while the calc()s keep the card's content box,
    // on-screen position and centering byte-identical.
    w: 'calc(100% + var(--web-ui-spacing-space-16))',
    // Literal (not `calc(${PRODUCT_CARD_MAX_WIDTH_DESKTOP} + …)`) so Panda's static extractor
    // sees it; mirrors PRODUCT_CARD_MAX_WIDTH_DESKTOP plus the 16px padding added below.
    maxW: { lg: 'calc(calc(var(--web-ui-spacing-space-56) * 10) + var(--web-ui-spacing-space-16))' },
    padding: 'space-8',
    margin: '-space-8',
    // Off-screen cards are skipped during style/layout recalc, so the forced reflows the filter
    // sheet's Radix/vaul mount-measures trigger stop traversing the whole grid (~7k elements per
    // recalc in traces). Intrinsic sizes mirror the skeleton; 'auto' keeps the real size once a
    // card has rendered.
    contentVisibility: 'auto',
    containIntrinsicSize: { base: 'auto 29.3rem', lg: 'auto 50rem' },
  },
})

type SubCollectionProductCardItem = NonNullable<SubCollectionSectionProps['subCollectionData']['productCards']>[number]

interface SubCollectionProductCardProps {
  productCard: SubCollectionProductCardItem
  index: number
  /** Initial swatch color value — used by the parent for the React key and here for `product_color`. */
  initialColorValue: string | undefined
  additionalAttributes?: Record<string, string>
  collectionGender: SubCollectionSectionProps['collectionGender']
  productViewTracker: string
  tracker: string | undefined
  selectorKey: string | undefined
  eagerLoadImage: boolean
  showSecondImageOnProductTiles: boolean
  enableImageCarousel: boolean
}

// Memoized per-card wrapper. The grid maps over `productCards`, so the per-card
// `productClickTrackConfig` cannot be `useMemo`'d in the loop; it's built here from stable primitive
// inputs behind a `memo` boundary so the wrapped ProductCard's shallow-compare skips re-renders on
// parent state the tile doesn't depend on (e.g. a loader flip). `index` is a dep so the tracked
// position stays correct if the list reorders. Adds no DOM node, so the grid's `& > *` still targets it.
const SubCollectionProductCard = memo(function SubCollectionProductCard({
  productCard,
  index,
  initialColorValue,
  additionalAttributes,
  collectionGender,
  productViewTracker,
  tracker,
  selectorKey,
  eagerLoadImage,
  showSecondImageOnProductTiles,
  enableImageCarousel,
}: SubCollectionProductCardProps) {
  // Matches the server-side `getCollectionPageDataPL` handler (`collection-page-data-pl.ts`) that
  // gates this CLP's own SVD-shape decision on the same flag.
  const isSvdEnabled = useIsSvdEnabled()
  // Legacy parity (sub-collection-section-legacy.tsx, STRFRNT-16872): the SVD branch's click
  // context omits `product_color`; only the non-SVD card carries it.
  const productClickTrackConfig = useMemo(
    () => ({
      trackInternal: true,
      trackGA: true,
      category: TRACKING_CATEGORY_NAVIGATION,
      action: 'click_subcollection_product',
      context: {
        product_id: productCard.product.relatedIds.spid,
        product_position: index,
        ...trackingMetaData.context,
      },
      value: productCard.product.slug,
    }),
    [productCard, index],
  )
  const legacyCardClickTrackConfig = useMemo(
    () => ({
      ...productClickTrackConfig,
      context: { product_color: initialColorValue, ...productClickTrackConfig.context },
    }),
    [productClickTrackConfig, initialColorValue],
  )

  // TEMPORARY (STRFRNT-15810 · CLP SVD preview): on DS CLPs, SVD-flow users may receive SVD-shaped
  // cards from PL — render the native SVD card. `isPlSvdCard` shape-guards on anchor data, so a
  // legacy-shaped card (no anchor attributes) always falls through to the DS ProductCard below.
  // Remove once every PL environment emits the SVD shape unconditionally.
  if (isSvdEnabled && isPlSvdCard(productCard.product)) {
    return (
      // `showWhiteGloveDeliveryTag` (passed on the DS branch below) is deliberately NOT forwarded, and
      // that is correct rather than a gap — resolved 2026-08-25. `migrate_white_glove_delivery_tag` is
      // at 100%, and with that gate on BOTH reference cards ignore the prop entirely: their
      // `deliveryLabel` takes the tag-derived arm (`product-card.tsx:354`), and the prop only appears
      // in the dead gate-off arm. The SVD card derives the chip the same way from the same tag union
      // (`use-svd-product-card.ts:261`), so the arms are at parity by construction. Forwarding the prop
      // here would have no effect on any card; the prop and both gate-off arms are dead code, to be
      // removed with the rest of the migration cleanup.
      <SvdProductCard
        product={mapPlSvdCard(productCard.product as unknown as PlSvdCard)}
        productClickTrackConfig={productClickTrackConfig}
        productViewTracker={productViewTracker}
        eagerLoadImage={eagerLoadImage}
        enableImageCarousel={enableImageCarousel}
        isPageEligibleForHolidayTag
        // SAND-6848: legacy `/products/enrich` tags are scoped per color name, not per-variant/SKU,
        // and disagree with the SVD wire's own tags — the swap ~1s after mount is the tag "flash".
        // Wire tags are already fresh (`use-svd-product-card.ts`'s `??` fallbacks), so skip enrich for
        // SVD-bucketed traffic. Keyed on `isSvdEnabled` (not `isPlSvdCard`, i.e. also applied to the
        // legacy-fallback `ProductCard` branch below) because the tag-refresh fetch queue is a
        // page-global last-caller-wins batch — a straggler legacy-shaped tile on this branch alone
        // would silently re-enable the refresh for every SVD tile on the page.
        skipTagRefresh={isSvdEnabled}
      />
    )
  }

  return (
    <ProductCard
      productCard={productCard}
      gender={collectionGender}
      productViewTracker={productViewTracker}
      productClickTrackConfig={legacyCardClickTrackConfig}
      additionalAttributes={additionalAttributes}
      selectorKey={selectorKey}
      eagerLoadImage={eagerLoadImage}
      tracker={tracker}
      showSecondImageOnProductTiles={showSecondImageOnProductTiles}
      enableImageCarousel={enableImageCarousel}
      showWhiteGloveDeliveryTag
      isPageEligibleForHolidayTag={true}
      skipTagRefresh={isSvdEnabled}
    />
  )
})

SubCollectionProductCard.displayName = 'SubCollectionProductCard'

export const SubCollectionSectionDS = forwardRef<HTMLDivElement, SubCollectionSectionProps>(
  (
    {
      subCollectionData,
      className,
      isFirstCollection = true,
      tracker,
      additionalAttributes,
      collectionGender,
      productViewTracker,
      showSecondImageOnProductTiles = false,
      enableImageCarousel = false,
      showShimmerOnProductCards = false,
    },
    ref,
  ) => {
    const { deviceType } = useDeviceInfo()
    const isDesktop = deviceType === DEVICE_TYPES.DESKTOP
    const productCards = subCollectionData.productCards

    // const subTitleProps = useMemo(
    //   () => RichTextDomain.makeRichTextProps(subCollectionData.subTitle),
    //   [subCollectionData.subTitle],
    // )

    const skeletonGridClass = useMemo(
      () =>
        css({
          display: 'grid',
          w: 'full',
          gridTemplateColumns: {
            base: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          justifyItems: 'center',
          // Mirrors gridClass so the layout doesn't shift when the skeleton swaps for cards.
          columnGap: { base: 'space-16', lg: 'space-24' },
          rowGap: { base: 'space-32', lg: 'space-48' },
          '& > *': {
            w: 'full',
            maxW: { lg: PRODUCT_CARD_MAX_WIDTH_DESKTOP },
          },
          px: { base: 'space-16', lg: 'space-48' },
          pt: '0',
          pb: 'space-100',
        }),
      [],
    )

    const outerClass = useMemo(() => {
      return css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        w: 'full',
        maxWidth: '1504px',
        mx: 'auto',
        pt: '0',
        pb: { base: 'space-80', lg: 'space-100' },
        borderTopWidth: {
          base: '0',
          lg: '0',
        },
        borderTopStyle: 'solid',
        borderTopColor: 'lightgray',
        borderBottomWidth: {
          lg: '0',
        },
        borderBottomStyle: 'solid',
        borderBottomColor: 'darkgray',
      })
    }, [])

    if (showShimmerOnProductCards) {
      return (
        <div className={skeletonGridClass} data-testid="product-card-skeleton">
          {new Array(isDesktop ? 8 : 4).fill(0).map((_, index) => (
            <Skeleton key={index} aria-hidden css={skeletonItemCss} />
          ))}
        </div>
      )
    }

    const showBorderBottomAttr = false

    return (
      <div
        ref={ref}
        data-testid="sub-collection-section-wrapper"
        data-content-type="sub-collection"
        data-show-border-bottom={String(showBorderBottomAttr)}
        className={classnames(outerClass, className)}
      >
        <div className={innerClass}>
          <div className={gridClass} data-content-type="products">
            {productCards?.map((productCard, idx) => {
              const initialSelectedVariant = getInitialSelectedVariant({
                cardVariants: productCard.product.cardVariants,
              })
              const initialColorValue = initialSelectedVariant?.displayConfig.value
              // An SVD-shaped card carries its full VDA scope, and PL can serve the same product twice
              // with the same colour but different scopes (e.g. per size) — `${productId}_${colour}`
              // would collide there, and duplicate keys let React keep stale tiles across a filter
              // change (STRFRNT-15810). Shape-guarded, so control keeps the colour-suffixed key.
              // `mapPlSvdCard` is identity-cached, so this shares the child branch's mapped object.
              const key = isPlSvdCard(productCard.product)
                ? getSvdCardListKey(mapPlSvdCard(productCard.product as unknown as PlSvdCard))
                : `${productCard.product.productId}_${initialColorValue}`
              return (
                <SubCollectionProductCard
                  key={key}
                  productCard={productCard}
                  index={idx}
                  initialColorValue={initialColorValue}
                  additionalAttributes={additionalAttributes}
                  collectionGender={collectionGender}
                  productViewTracker={productViewTracker}
                  selectorKey={isFirstCollection && idx === 0 ? QUINCE_BOT_LOCATORS.COLLECTION.MOVE_TO_PDP : undefined}
                  eagerLoadImage={isFirstCollection && idx < 4}
                  tracker={tracker && `${tracker}__${subCollectionData.title}__${idx}`}
                  showSecondImageOnProductTiles={showSecondImageOnProductTiles}
                  enableImageCarousel={enableImageCarousel}
                />
              )
            })}
          </div>
        </div>
      </div>
    )
  },
)

SubCollectionSectionDS.displayName = 'SubCollectionSectionDS'
